// SPDX-License-Identifier: AGPL-3.0-or-later

import {COSMETIC_ASSET_POLICY} from '@fluxer/constants/src/AssetFormatPolicy';
import {EXPERIMENT_BUCKET_RESOLUTION} from '@fluxer/schema/src/domains/experiment/ExperimentBucket';
import {base64LengthForBytes, createBase64StringType} from '@fluxer/schema/src/primitives/FileValidators';
import {RegionTarget} from '@fluxer/schema/src/primitives/RegionTargetSchemas';
import {createStringType, SnowflakeStringType, SnowflakeType} from '@fluxer/schema/src/primitives/SchemaPrimitives';
import {z} from 'zod';

// Micup: the interest picker new accounts see once; every option points at one guild the account joins.
export const INTEREST_ONBOARDING_MAX_REGIONS = 20;
export const INTEREST_ONBOARDING_MAX_OPTIONS_PER_REGION = 40;
export const INTEREST_ONBOARDING_MAX_SELECTIONS = 10;
// A region with fewer enabled options takes all of them; the region is known only once the request is read.
export const INTEREST_ONBOARDING_MIN_SELECTIONS = 2;
export const INTEREST_ONBOARDING_MAX_GUILDS_PER_OPTION = 5;
// Distinct guilds one account joins through the picker; a guild of several chosen options is joined once.
export const INTEREST_ONBOARDING_MAX_JOINED_GUILDS = 20;
// Registration auto join rules: a new account joins the guild of the rule its country matches.
export const INTEREST_ONBOARDING_MAX_AUTO_JOIN_RULES = 50;

function isUnique(values: ReadonlyArray<string>): boolean {
	return new Set(values).size === values.length;
}

const CountryCode = z
	.string()
	.trim()
	.toUpperCase()
	.regex(/^[A-Z]{2}$/)
	.describe('ISO 3166-1 alpha-2 country code');

const LanguageCode = z
	.string()
	.trim()
	.toLowerCase()
	.regex(/^[a-z]{2,3}$/)
	.describe('Language part of an app locale, such as tr or en');

const RegionName = createStringType(1, 64).describe(
	'Region label, also shown in the region switcher (1-64 characters)',
);
const RegionCountries = z
	.array(CountryCode)
	.max(250)
	.refine(isUnique, 'Countries must be unique')
	.describe('IP countries that get this region; a country belongs to at most one region');
const RegionLanguages = z
	.array(LanguageCode)
	.max(20)
	.refine(isUnique, 'Languages must be unique')
	.describe('App languages that get this region when no region lists the IP country');
const RegionIsDefault = z
	.boolean()
	.describe('Shown when neither the country nor the language matches; at most one region');

const OptionName = createStringType(1, 40).describe('Card title (1-40 characters)');
const OptionGroup = createStringType(1, 40).describe(
	'Heading the card is listed under, such as Games or Public servers (1-40 characters); null for none',
);
const OptionAsset = createBase64StringType(1, base64LengthForBytes(COSMETIC_ASSET_POLICY.maxBytes)).describe(
	'Base64 PNG, APNG or WebP logo, 96-480 px each side, 512 KB at most; SVG is refused',
);
const OptionGuild = createStringType(1, 100).describe(
	'An invite code, an invite link or a guild ID; the option stores the guild ID',
);
const OptionGuilds = z
	.array(OptionGuild)
	.min(1)
	.max(INTEREST_ONBOARDING_MAX_GUILDS_PER_OPTION)
	.refine(isUnique, 'Guilds must be unique')
	.describe(`Guilds a chosen option joins, 1-${INTEREST_ONBOARDING_MAX_GUILDS_PER_OPTION}`);
const OptionPosition = z.number().int().min(0).max(999).describe('Order in the region, lowest first (0-999)');

function hasAnyField(value: Record<string, unknown>): boolean {
	return Object.values(value).some((field) => field !== undefined);
}

export const InterestOnboardingQuery = z.object({
	region_id: SnowflakeStringType.optional().describe('Show this region instead of the one the request resolves to'),
});

export type InterestOnboardingQuery = z.infer<typeof InterestOnboardingQuery>;

export const CompleteInterestOnboardingRequest = z.object({
	region_id: SnowflakeStringType.describe('The region the options come from'),
	option_ids: z
		.array(SnowflakeStringType)
		.min(1)
		.max(INTEREST_ONBOARDING_MAX_SELECTIONS)
		.refine(isUnique, 'Options must be unique')
		.describe(
			`Chosen options, ${INTEREST_ONBOARDING_MIN_SELECTIONS}-${INTEREST_ONBOARDING_MAX_SELECTIONS}, or every option of a region with fewer than ${INTEREST_ONBOARDING_MIN_SELECTIONS}`,
		),
});

export type CompleteInterestOnboardingRequest = z.infer<typeof CompleteInterestOnboardingRequest>;

export const UpdateInterestOnboardingSettingsRequest = z
	.object({
		enabled: z
			.boolean()
			.optional()
			.describe('On shows the picker to accounts created since the picker was first turned on; off hides it'),
		official_guild: OptionGuild.nullable()
			.optional()
			.describe(
				'The official guild every account that finishes the picker joins first: an invite code, an invite link or a guild ID; null turns it off',
			),
		existing_accounts_rollout_basis_points: z
			.number()
			.int()
			.min(0)
			.max(EXPERIMENT_BUCKET_RESOLUTION)
			.optional()
			.describe(
				'Share of the accounts created before the first turn-on that must finish the picker, in basis points (0-10000); the same accounts stay in as it grows',
			),
		shown_in: RegionTarget.min(1)
			.optional()
			.describe(
				'Countries whose accounts are shown the picker (by the IP country when the session starts); WORLDWIDE for every other country',
			),
		skip_enabled: z.boolean().optional().describe('Whether the picker offers Skip'),
	})
	.refine(hasAnyField, 'At least one field is required');

export type UpdateInterestOnboardingSettingsRequest = z.infer<typeof UpdateInterestOnboardingSettingsRequest>;

export const CreateInterestRegionRequest = z.object({
	name: RegionName,
	countries: RegionCountries.default([]),
	languages: RegionLanguages.default([]),
	is_default: RegionIsDefault.default(false),
	enabled: z.boolean().default(true),
});

export type CreateInterestRegionRequest = z.infer<typeof CreateInterestRegionRequest>;

export const UpdateInterestRegionRequest = z
	.object({
		name: RegionName.optional(),
		countries: RegionCountries.optional(),
		languages: RegionLanguages.optional(),
		is_default: RegionIsDefault.optional(),
		enabled: z.boolean().optional(),
	})
	.refine(hasAnyField, 'At least one field is required');

export type UpdateInterestRegionRequest = z.infer<typeof UpdateInterestRegionRequest>;

export const CreateInterestOptionRequest = z.object({
	name: OptionName,
	group: OptionGroup.nullable().default(null),
	asset: OptionAsset,
	guilds: OptionGuilds,
	position: OptionPosition.default(0),
	enabled: z.boolean().default(true),
});

export type CreateInterestOptionRequest = z.infer<typeof CreateInterestOptionRequest>;

export const UpdateInterestOptionRequest = z
	.object({
		name: OptionName.optional(),
		group: OptionGroup.nullable().optional(),
		asset: OptionAsset.nullable().optional().describe('A new logo replaces the current one; null removes it'),
		guilds: OptionGuilds.optional(),
		position: OptionPosition.optional(),
		enabled: z.boolean().optional(),
	})
	.refine(hasAnyField, 'At least one field is required');

export type UpdateInterestOptionRequest = z.infer<typeof UpdateInterestOptionRequest>;

const AutoJoinCountries = RegionTarget.min(1).describe(
	'Countries whose new accounts join this guild; WORLDWIDE for every country no other rule names. A country is in at most one rule, WORLDWIDE too',
);
const AutoJoinInvite = createStringType(1, 100).describe(
	'An invite code or link of the guild; it must never expire and have no use limit',
);

export const CreateInterestAutoJoinRequest = z.object({
	countries: AutoJoinCountries,
	invite: AutoJoinInvite,
});

export type CreateInterestAutoJoinRequest = z.infer<typeof CreateInterestAutoJoinRequest>;

export const UpdateInterestAutoJoinRequest = z
	.object({
		countries: AutoJoinCountries.optional(),
		invite: AutoJoinInvite.optional(),
	})
	.refine(hasAnyField, 'At least one field is required');

export type UpdateInterestAutoJoinRequest = z.infer<typeof UpdateInterestAutoJoinRequest>;

export const InterestAutoJoinRuleParam = z.object({
	rule_id: SnowflakeType.describe('The ID of the auto join rule'),
});

export type InterestAutoJoinRuleParam = z.infer<typeof InterestAutoJoinRuleParam>;

export const InterestRegionIdParam = z.object({
	region_id: SnowflakeType.describe('The ID of the interest region'),
});

export type InterestRegionIdParam = z.infer<typeof InterestRegionIdParam>;

export const InterestOptionParam = z.object({
	region_id: SnowflakeType.describe('The ID of the interest region'),
	option_id: SnowflakeType.describe('The ID of the interest option'),
});

export type InterestOptionParam = z.infer<typeof InterestOptionParam>;
